import { Link, Navigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Calendar, Clock, BookOpen, Check } from 'lucide-react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import InsightContent from '@/components/InsightContent';
import { getInsightBySlug } from '@/data/insights';
import { services } from '@/data/services';
import { IMAGE_WIDTHS, buildSrc, buildSrcSet } from '@/lib/responsiveImage';

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

const RatgeberDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const insight = slug ? getInsightBySlug(slug) : undefined;

  if (!insight) {
    return <Navigate to="/ratgeber" replace />;
  }

  const heroImage = buildSrc(insight.heroImage, 1024);
  const heroImageSrcSet = buildSrcSet(insight.heroImage, IMAGE_WIDTHS.card);

  const relatedServices = (insight.relatedServices || [])
    .map((slug) => services.find((service) => service.slug === slug))
    .filter(Boolean);

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main id="main-content" tabIndex={-1}>
        <section className="pt-24 pb-16 section-dark">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="grid lg:grid-cols-[1.1fr_0.9fr] gap-10 items-center"
            >
              <div>
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <span className="text-primary font-medium text-sm uppercase tracking-wider">{insight.category}</span>
                  {insight.series && (
                    <span className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-3 py-1 text-xs text-white/85">
                      <BookOpen className="w-3.5 h-3.5" aria-hidden="true" />
                      Serie „{insight.series.name}" · Teil {insight.series.part} von {insight.series.total}
                    </span>
                  )}
                </div>
                <h1 className="text-3xl md:text-5xl font-display font-bold mt-2 mb-6 text-white">
                  {insight.title}
                </h1>
                <p className="text-white/90 text-lg md:text-xl max-w-2xl">
                  {insight.excerpt}
                </p>
                <div className="flex flex-wrap items-center gap-4 text-sm text-white/90 mt-6">
                  <span className="inline-flex items-center gap-2">
                    <Calendar className="w-4 h-4" aria-hidden="true" />
                    {formatDate(insight.datePublished)}
                  </span>
                  <span className="inline-flex items-center gap-2">
                    <Clock className="w-4 h-4" aria-hidden="true" />
                    {insight.readingTime}
                  </span>
                  <span className="text-white/90">{insight.author.name}</span>
                </div>
              </div>

              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-white/10">
                <img
                  src={heroImage}
                  srcSet={heroImageSrcSet}
                  sizes="(min-width: 1024px) 40vw, 100vw"
                  alt={insight.heroImageAlt}
                  width={1024}
                  height={730}
                  decoding="async"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </section>

        <section className="py-20 bg-background">
          <div className="container mx-auto px-6">
            <div className="max-w-3xl mx-auto">
              {insight.series && (
                <nav
                  aria-label="Artikel-Serie"
                  className="mb-12 rounded-2xl border border-border bg-card p-6 shadow-sm"
                >
                  <div className="flex items-center gap-2 text-primary text-xs uppercase tracking-wider font-semibold mb-3">
                    <BookOpen className="w-4 h-4" aria-hidden="true" />
                    Serie „{insight.series.name}" · {insight.series.total} Teile
                  </div>
                  <ol className="space-y-2">
                    {insight.series.parts.map((part) => {
                      const isCurrent = part.part === insight.series!.part;
                      const content = (
                        <span className="flex items-start gap-3">
                          <span
                            className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold shrink-0 ${
                              isCurrent
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-muted text-muted-foreground'
                            }`}
                          >
                            {isCurrent ? <Check className="w-3.5 h-3.5" aria-hidden="true" /> : part.part}
                          </span>
                          <span
                            className={`text-base ${
                              isCurrent ? 'font-semibold text-foreground' : 'text-muted-foreground'
                            }`}
                          >
                            {part.title}
                            {isCurrent && (
                              <span className="ml-2 text-xs uppercase tracking-wider text-primary">
                                Sie sind hier
                              </span>
                            )}
                          </span>
                        </span>
                      );
                      return (
                        <li key={part.part}>
                          {part.slug && !isCurrent ? (
                            <Link
                              to={`/ratgeber/${part.slug}`}
                              className="block rounded-lg -mx-2 px-2 py-1 hover:bg-muted transition-colors"
                            >
                              {content}
                            </Link>
                          ) : (
                            <div className="px-2 py-1">{content}</div>
                          )}
                        </li>
                      );
                    })}
                  </ol>
                  <p className="mt-4 text-xs text-muted-foreground italic">
                    Die weiteren Teile der Serie erscheinen in den kommenden Wochen.
                  </p>
                </nav>
              )}

              {insight.contentBlocks && insight.contentBlocks.length > 0 ? (
                <InsightContent blocks={insight.contentBlocks} />
              ) : (
                <div className="space-y-12">
                  {(insight.sections ?? []).map((section) => (
                    <div key={section.heading}>
                      <h2 className="text-2xl md:text-3xl font-display font-bold text-foreground mb-4">
                        {section.heading}
                      </h2>
                      <div className="space-y-4 text-muted-foreground text-lg leading-relaxed">
                        {section.paragraphs.map((paragraph, index) => (
                          <p key={index}>{paragraph}</p>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {insight.sources && insight.sources.length > 0 && (
                <aside
                  aria-label="Quellen"
                  className="mt-16 pt-8 border-t border-border"
                >
                  <h2 className="text-lg font-display font-semibold text-foreground mb-4 uppercase tracking-wider">
                    Welche Quellen haben wir genutzt?
                  </h2>
                  <ol className="space-y-2 text-sm text-muted-foreground list-decimal list-inside marker:text-primary marker:font-semibold">
                    {insight.sources.map((source, i) => (
                      <li key={i} className="pl-1">
                        {source.url ? (
                          <a
                            href={source.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-foreground hover:text-primary underline underline-offset-2"
                          >
                            {source.label}
                          </a>
                        ) : (
                          <span className="text-foreground">{source.label}</span>
                        )}
                        {source.note && (
                          <span className="ml-2 text-xs uppercase tracking-wider text-muted-foreground">
                            · {source.note}
                          </span>
                        )}
                      </li>
                    ))}
                  </ol>
                </aside>
              )}
            </div>
          </div>
        </section>

        {relatedServices.length > 0 && (
          <section className="py-20 section-dark">
            <div className="container mx-auto px-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center max-w-3xl mx-auto mb-10"
              >
                <h2 className="text-2xl md:text-3xl font-display font-bold text-white mb-4">
                  Welche Leistungen passen zum Thema?
                </h2>
                <p className="text-white/90">
                  Vertiefen Sie das Thema mit unseren Leistungen.
                </p>
              </motion.div>
              <div className="grid md:grid-cols-3 gap-6">
                {relatedServices.map((service) => (
                  <Link
                    key={service!.slug}
                    to={`/leistungen/${service!.slug}`}
                    className="bg-card rounded-2xl p-6 border border-border hover:border-primary/50 transition-colors shadow-lg"
                  >
                    <h3 className="text-lg font-display font-semibold text-card-foreground mb-2">
                      {service!.title}
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      {service!.description}
                    </p>
                    <span className="text-primary text-sm font-medium inline-flex items-center gap-1 mt-4">
                      Leistung ansehen
                      <ArrowRight className="w-4 h-4" aria-hidden="true" />
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="py-16 bg-background">
          <div className="container mx-auto px-6">
            <div className="bg-card rounded-2xl p-8 border border-border shadow-lg flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <h3 className="text-2xl font-display font-bold text-card-foreground mb-2">
                  Projekt geplant?
                </h3>
                <p className="text-muted-foreground">
                  Lassen Sie uns gemeinsam Ihre Ziele definieren und die passende digitale Lösung entwickeln.
                </p>
              </div>
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors shadow-lg"
              >
                Kontakt aufnehmen
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default RatgeberDetailPage;
